import { useCallback, useSyncExternalStore } from 'react'
import useDeviceClass from 'hooks/useDeviceClass'

export type AppMode = 'field' | 'desktop'

const STORAGE_KEY = 'mdt.appMode'

/**
 * Which app the user wants: the field app or the full desktop site.
 *
 * **On a phone there is no choice, and that is deliberate.** The field app is
 * mobile MYDealTeams now, not an alternative to it. Two homes meant two places
 * to maintain the same navigation and two answers to "where is Projects", and
 * the desktop home on a phone was never the better one. A stored preference
 * that could strand a user in the worse app is not a feature.
 *
 * Above a phone this stays a **preference, not a measurement.** The device
 * class says what the hardware is; it does not say what the person wants to do
 * with it. A tablet user reviewing financials wants the desktop site, and a
 * laptop user preparing for a site visit may well want the field app.
 *
 * Inferring it from pixels alone is what produced the bug this hook was written
 * for: an iPad user could reach a screen in landscape and not in portrait,
 * because a width threshold decided for them. A stored choice with a visible
 * toggle dissolves that, and rotating the device changes nothing.
 *
 * So: a phone is always the field app; a tablet or desktop keeps its choice,
 * with the device class supplying only the default for someone who has never
 * chosen.
 */

const read = (): AppMode | null => {
	try {
		const stored = localStorage.getItem(STORAGE_KEY)
		return stored === 'field' || stored === 'desktop' ? stored : null
	} catch {
		// Private browsing throws rather than returning null.
		return null
	}
}

/** Lets `useSyncExternalStore` re-render every consumer when the mode changes. */
const listeners = new Set<() => void>()
const subscribe = (listener: () => void) => {
	listeners.add(listener)
	return () => listeners.delete(listener)
}
const emit = () => listeners.forEach(listener => listener())

export const setStoredAppMode = (mode: AppMode): void => {
	try {
		localStorage.setItem(STORAGE_KEY, mode)
	} catch {
		/* The toggle still works for this session; it just will not persist. */
	}
	emit()
}

export const clearStoredAppMode = (): void => {
	try {
		localStorage.removeItem(STORAGE_KEY)
	} catch {
		/* ignore */
	}
	emit()
}

export interface UseAppModeResult {
	/** The mode in force: on a phone always `field`, else the stored choice. */
	mode: AppMode
	/** True when the user picked this, rather than it being inferred. */
	isExplicit: boolean
	/** True when this device offers a choice at all. False on a phone. */
	canChoose: boolean
	/** No-op on a phone, where there is nothing to choose. */
	setMode: (mode: AppMode) => void
}

const useAppMode = (): UseAppModeResult => {
	const deviceClass = useDeviceClass()
	const stored = useSyncExternalStore(subscribe, read, () => null)

	const isPhone = deviceClass === 'phone'

	const setMode = useCallback(
		(next: AppMode) => {
			// Writing a preference a phone will never read would be a lie that
			// surfaces later, on a tablet, as a mode the user does not remember
			// choosing.
			if (isPhone) return
			setStoredAppMode(next)
		},
		[isPhone],
	)

	// A phone is the field app, whatever is in storage. Users who chose
	// "desktop" before this change are carried back automatically: the stored
	// value is ignored rather than migrated, so nothing has to be cleaned up and
	// their choice is still there if they later open a tablet.
	if (isPhone) return { mode: 'field', isExplicit: false, canChoose: false, setMode }

	return {
		mode: stored ?? 'desktop',
		isExplicit: stored !== null,
		canChoose: true,
		setMode,
	}
}

export default useAppMode
